import * as Phaser from "phaser"
import { GAME_HEIGHT, GAME_WIDTH } from "../../data/constants"
import { ENEMY_DEFINITIONS } from "../../data/enemies"
import type { EnemyData, GameSprite, SnakePartState } from "../../types"
import { transitionSnakeFlightPhase } from "../enemy-system"
import { type EnemyState, StatefulEnemyRuntime } from "../enemy-system"

type SnakePartFsmState =
  | "enter"
  | "swoop"
  | "attack-run"
  | "retreat"
  | "head-charge"
  | "head-recover"

const BOSS_Y_MIN = 50
const BOSS_Y_MAX = 520

// Each role holds a distinct horizontal lane — head forward, tail back
const HOLD_X: Record<"head" | "body" | "tail", number> = {
  head: Math.round(GAME_WIDTH * 0.6),
  body: Math.round(GAME_WIDTH * 0.74),
  tail: Math.round(GAME_WIDTH * 0.86),
}

const RETREAT_X: Record<"head" | "body" | "tail", number> = {
  head: Math.round(GAME_WIDTH * 0.68),
  body: Math.round(GAME_WIDTH * 0.8),
  tail: Math.round(GAME_WIDTH * 0.9),
}

function clampY(y: number): number {
  return Phaser.Math.Clamp(y, BOSS_Y_MIN, BOSS_Y_MAX)
}

function getSpec(enemyData: EnemyData) {
  const movement = ENEMY_DEFINITIONS[enemyData.type].movement
  if (movement.action !== "snake-part") return null
  return movement
}

function getRole(partState: SnakePartState): "head" | "body" | "tail" {
  return partState.partRole
}

export class SnakePartEnemy extends StatefulEnemyRuntime<SnakePartFsmState> {
  private chargeTargetY = GAME_HEIGHT * 0.5

  constructor(sprite: GameSprite, enemyData: EnemyData) {
    const states: EnemyState<SnakePartFsmState>[] = [
      // ── ENTER ─────────────────────────────────────────────────────────────
      {
        name: "enter",
        behaviorState: "enter",
        canFire: false,
        update: (ctx) => {
          const partState = ctx.enemyData.supportState as
            | SnakePartState
            | undefined
          if (!partState) return null
          const spec = getSpec(ctx.enemyData)
          if (!spec) return null

          const role = getRole(partState)
          const targetX = HOLD_X[role]
          // Parts that split from the boss snap into position faster
          const lerpSpeed = partState.fromSplit ? 0.07 : 0.04

          partState.flightTime += ctx.delta
          partState.phaseTimer -= ctx.delta

          const homeY = ctx.enemyData.homeY ?? ctx.sprite.y
          const sw =
            Math.sin(
              (partState.flightTime * Math.PI * 2) / spec.swoopPeriodMs,
            ) * spec.swoopAmplitude

          ctx.sprite.x += (targetX - ctx.sprite.x) * lerpSpeed
          ctx.sprite.y += (clampY(homeY + sw) - ctx.sprite.y) * 0.06
          ctx.sprite.setAngle(
            Phaser.Math.Clamp(
              (clampY(homeY + sw) - ctx.sprite.y) * 0.6,
              -15,
              15,
            ),
          )

          if (partState.phaseTimer <= 0) {
            partState.swoopTargetY = clampY(
              ctx.playerY + (Math.random() - 0.5) * 100,
            )
            partState.phaseTimer = spec.swoopPeriodMs * 1.5
            transitionSnakeFlightPhase(ctx.sprite, partState, "swoop")
            return "swoop"
          }
          return null
        },
      },

      // ── SWOOP ─────────────────────────────────────────────────────────────
      {
        name: "swoop",
        behaviorState: "swoop",
        canFire: true,
        update: (ctx) => {
          const partState = ctx.enemyData.supportState as
            | SnakePartState
            | undefined
          if (!partState) return null
          const spec = getSpec(ctx.enemyData)
          if (!spec) return null

          const role = getRole(partState)
          const targetX = HOLD_X[role]

          partState.flightTime += ctx.delta
          partState.phaseTimer -= ctx.delta

          const homeY = ctx.enemyData.homeY ?? ctx.sprite.y
          const sw =
            Math.sin(
              (partState.flightTime * Math.PI * 2) / spec.swoopPeriodMs,
            ) * spec.swoopAmplitude

          // Tail gets extra jitter for an erratic feel
          const jitter =
            role === "tail"
              ? Math.sin(
                  (partState.flightTime * Math.PI * 5.3) / spec.swoopPeriodMs,
                ) *
                spec.swoopAmplitude *
                0.35
              : 0

          const swoopTarget = clampY(
            homeY +
              sw +
              jitter +
              (ctx.playerY - homeY) * spec.trackSpeed * 0.03,
          )
          ctx.sprite.y += (swoopTarget - ctx.sprite.y) * 0.07
          ctx.sprite.x += (targetX - ctx.sprite.x) * 0.015
          ctx.sprite.setAngle(
            Phaser.Math.Clamp(
              (clampY(homeY + sw) - ctx.sprite.y) * 0.6,
              -15,
              15,
            ),
          )

          if (partState.phaseTimer <= 0) {
            partState.swoopTargetY = clampY(ctx.playerY)
            partState.phaseTimer = spec.attackRunDurationMs
            // Head does a full screen-crossing dive charge instead of attack-run
            if (role === "head") {
              this.chargeTargetY = clampY(
                ctx.playerY + (Math.random() - 0.5) * 60,
              )
              ctx.sprite.setTint(0xff3366)
              transitionSnakeFlightPhase(ctx.sprite, partState, "attack-run")
              return "head-charge"
            }
            transitionSnakeFlightPhase(ctx.sprite, partState, "attack-run")
            return "attack-run"
          }
          return null
        },
      },

      // ── ATTACK-RUN (body: vertical sweep, tail: dart) ─────────────────────
      {
        name: "attack-run",
        behaviorState: "attack-run",
        canFire: true,
        update: (ctx) => {
          const partState = ctx.enemyData.supportState as
            | SnakePartState
            | undefined
          if (!partState) return null
          const spec = getSpec(ctx.enemyData)
          if (!spec) return null

          const role = getRole(partState)
          const targetX = HOLD_X[role]

          partState.flightTime += ctx.delta
          partState.phaseTimer -= ctx.delta

          if (role === "body") {
            // Body sweeps Y at its lane — acts as a moving wall
            const sweepTarget = clampY(
              ctx.playerY +
                Math.sin((partState.flightTime * Math.PI * 2) / 1800) * 140,
            )
            ctx.sprite.y +=
              (sweepTarget - ctx.sprite.y) *
              Math.min(0.09, spec.trackSpeed * 0.09)
            ctx.sprite.x += (targetX - ctx.sprite.x) * 0.02
          } else {
            // Tail: dart toward player then snap back on timer
            const attackTargetX = ctx.playerX + 80
            const attackTargetY = clampY(partState.swoopTargetY)
            const dx = attackTargetX - ctx.sprite.x
            ctx.sprite.x +=
              Math.sign(dx) *
              Math.min(
                Math.abs(dx) * 0.1,
                spec.attackRunSpeed * (ctx.delta / 16),
              )
            ctx.sprite.y +=
              (attackTargetY - ctx.sprite.y) *
              Math.min(0.14, spec.trackSpeed * 0.07)
          }

          ctx.sprite.setAngle(
            Phaser.Math.Clamp(
              (ctx.sprite.y - (ctx.enemyData.homeY ?? ctx.sprite.y)) * 0.4,
              -15,
              15,
            ),
          )

          if (partState.phaseTimer <= 0) {
            ctx.enemyData.homeY = ctx.sprite.y
            partState.phaseTimer = spec.retreatDurationMs
            transitionSnakeFlightPhase(ctx.sprite, partState, "retreat")
            return "retreat"
          }
          return null
        },
      },

      // ── HEAD CHARGE (screen-crossing dive, Gradius style) ─────────────────
      {
        name: "head-charge",
        behaviorState: "attacking",
        canFire: true,
        update: (ctx) => {
          const partState = ctx.enemyData.supportState as
            | SnakePartState
            | undefined
          if (!partState) return null
          const spec = getSpec(ctx.enemyData)
          if (!spec) return null

          partState.phaseTimer -= ctx.delta
          const dt = ctx.delta / 16.6
          const chargeSpeed = spec.attackRunSpeed * 1.8 * dt

          ctx.sprite.x -= chargeSpeed
          ctx.sprite.y = Phaser.Math.Clamp(
            this.chargeTargetY + Math.sin(ctx.stateTime / 70) * 10,
            BOSS_Y_MIN,
            BOSS_Y_MAX,
          )
          ctx.sprite.setAngle(-12)

          if (ctx.sprite.x < -160 || partState.phaseTimer <= 0) {
            ctx.sprite.clearTint()
            return "head-recover"
          }
          return null
        },
        exit: (ctx) => {
          ctx.sprite.clearTint()
          ctx.sprite.setAngle(0)
        },
      },

      // ── HEAD RECOVER (re-enter from right) ────────────────────────────────
      {
        name: "head-recover",
        behaviorState: "flying",
        canFire: false,
        enter: (ctx) => {
          ctx.sprite.x = HOLD_X.head
          ctx.sprite.y = Phaser.Math.Clamp(
            this.chargeTargetY + (Math.random() - 0.5) * 100,
            BOSS_Y_MIN,
            BOSS_Y_MAX,
          )
          ctx.sprite.setAlpha(0)
          ctx.scene.tweens.add({ targets: ctx.sprite, alpha: 1, duration: 350 })
        },
        update: (ctx) => {
          const partState = ctx.enemyData.supportState as
            | SnakePartState
            | undefined
          const spec = getSpec(ctx.enemyData)
          if (!partState || !spec) return null

          const dt = ctx.delta / 16.6
          ctx.sprite.x = Phaser.Math.Linear(
            ctx.sprite.x,
            HOLD_X.head,
            1 - (1 - 0.09) ** (dt * 0.9),
          )

          if (
            ctx.stateTime >= 1000 &&
            Math.abs(ctx.sprite.x - HOLD_X.head) < 40
          ) {
            ctx.enemyData.homeY = ctx.sprite.y
            partState.swoopTargetY = clampY(
              ctx.playerY + (Math.random() - 0.5) * 100,
            )
            partState.phaseTimer = spec.swoopPeriodMs * 1.5
            transitionSnakeFlightPhase(ctx.sprite, partState, "swoop")
            return "swoop"
          }
          return null
        },
      },

      // ── RETREAT ───────────────────────────────────────────────────────────
      {
        name: "retreat",
        behaviorState: "retreat",
        canFire: true,
        update: (ctx) => {
          const partState = ctx.enemyData.supportState as
            | SnakePartState
            | undefined
          if (!partState) return null
          const spec = getSpec(ctx.enemyData)
          if (!spec) return null

          const role = getRole(partState)
          const retreatX = RETREAT_X[role]

          partState.flightTime += ctx.delta
          partState.phaseTimer -= ctx.delta

          const homeY = ctx.enemyData.homeY ?? ctx.sprite.y
          const sw =
            Math.sin(
              (partState.flightTime * Math.PI * 2) / spec.swoopPeriodMs,
            ) * spec.swoopAmplitude

          ctx.sprite.x += (retreatX - ctx.sprite.x) * 0.03
          ctx.sprite.y += (clampY(homeY + sw) - ctx.sprite.y) * 0.05
          ctx.sprite.setAngle(
            Phaser.Math.Clamp(
              (clampY(homeY + sw) - ctx.sprite.y) * 0.6,
              -15,
              15,
            ),
          )

          if (partState.phaseTimer <= 0) {
            ctx.enemyData.homeY = ctx.sprite.y
            partState.swoopTargetY = clampY(
              ctx.playerY + (Math.random() - 0.5) * 120,
            )
            partState.phaseTimer = spec.swoopPeriodMs * 1.5
            transitionSnakeFlightPhase(ctx.sprite, partState, "swoop")
            return "swoop"
          }
          return null
        },
      },
    ]

    super(sprite, enemyData, "enter", states)
  }
}
